<template>
    <!-- Crypto top-up (EVMPay). The coin tiles and network rows are built from the live wallet list, so a coin
         added or removed on the gateway shows up here without a code change. While an invoice is open the page
         asks the backend every 10 s how the gateway sees it (payment on the way, confirmations, paid). -->
    <section class="topup">
        <header class="topup__head">
            <div>
                <h1 class="topup__title">{{ t('topup.title') }}</h1>
                <p class="topup__sub">{{ t('topup.sub') }}</p>
            </div>
            <div class="topup__balance">
                <span>{{ t('topup.currentBalance') }}</span>
                <strong>${{ money(balance) }}</strong>
            </div>
        </header>

        <!-- ============ payment step: an invoice is open ============ -->
        <div v-if="invoice" class="topup__grid">
            <div class="panel pay">
                <div class="pay__top">
                    <h2 class="panel__title">{{ t('topup.sendPayment') }}</h2>
                    <span class="pay__state" :class="'is-' + invoiceState">{{ stateLabel }}</span>
                </div>

                <div class="pay__body">
                    <img v-if="invoice.qrCode" :src="invoice.qrCode" class="pay__qr" alt="Payment QR code" />
                    <div class="pay__fields">
                        <div class="field">
                            <span class="field__label">{{ t('topup.amountToSend') }}</span>
                            <div class="field__row">
                                <code class="field__value">{{ invoiceAmount }} {{ invoiceCoin.ticker }}</code>
                                <button type="button" class="copy" @click="copyText(invoiceAmount, t('topup.amountCopied'))">
                                    <v-icon icon="mdi-content-copy" size="15"></v-icon> {{ t('common.copy') }}
                                </button>
                            </div>
                        </div>
                        <div class="field">
                            <span class="field__label">{{ t('topup.toAddress') }}</span>
                            <div class="field__row">
                                <code class="field__value field__value--addr">{{ invoice.address }}</code>
                                <button type="button" class="copy" @click="copyText(invoice.address, t('topup.addressCopied'))">
                                    <v-icon icon="mdi-content-copy" size="15"></v-icon> {{ t('common.copy') }}
                                </button>
                            </div>
                        </div>
                        <div class="field">
                            <span class="field__label">{{ t('topup.network') }}</span>
                            <div class="field__row"><span class="field__plain">{{ invoiceNetwork }}</span></div>
                        </div>
                    </div>
                </div>

                <p class="pay__warn">
                    <v-icon icon="mdi-alert-outline" size="18"></v-icon>
                    <span v-html="warnHtml"></span>
                </p>

                <ol class="track">
                    <li class="is-done"><span class="track__dot"><v-icon icon="mdi-check" size="14"></v-icon></span>{{ t('topup.invoiceCreated') }}</li>
                    <li :class="invoiceState === 'completed' ? 'is-done' : (invoiceState === 'expired' ? 'is-off' : 'is-now')">
                        <span class="track__dot"><v-icon v-if="invoiceState === 'completed'" icon="mdi-check" size="14"></v-icon></span>
                        {{ invoiceState === 'expired' ? t('topup.noPayment') : (invoiceState === 'detected' ? confirmText : t('topup.waitingConfirm')) }}
                    </li>
                    <li :class="invoiceState === 'completed' ? 'is-done' : 'is-off'">
                        <span class="track__dot"><v-icon v-if="invoiceState === 'completed'" icon="mdi-check" size="14"></v-icon></span>
                        {{ t('topup.balanceCredited') }}
                    </li>
                </ol>

                <div class="pay__foot">
                    <span v-if="invoiceState === 'detected'" class="pay__timer">
                        <v-icon icon="mdi-progress-check" size="16"></v-icon> {{ t('topup.detectedMsg') }}
                    </span>
                    <span v-else-if="invoiceState === 'waiting'" class="pay__timer">
                        <v-icon icon="mdi-clock-outline" size="16"></v-icon> {{ t('topup.invoiceOpen', { time: timeLeft }) }}
                    </span>
                    <span v-else-if="invoiceState === 'completed'" class="pay__timer">{{ t('topup.paidMsg') }}</span>
                    <span v-else class="pay__timer">{{ t('topup.expiredMsg') }}</span>
                    <button type="button" class="btn-ghost" @click="closeInvoice">
                        {{ invoiceState === 'waiting' || invoiceState === 'detected' ? t('topup.backToOptions') : t('topup.startAnother') }}
                    </button>
                </div>
                <p v-if="invoiceState === 'waiting' || invoiceState === 'detected'" class="pay__hint">
                    {{ t('topup.leaveHint') }}
                </p>
            </div>

            <aside class="side">
                <div class="panel">
                    <h2 class="panel__title">{{ t('topup.thisTopUp') }}</h2>
                    <dl class="sum">
                        <div><dt>{{ t('topup.topUp') }}</dt><dd>${{ money(invoice.amount) }}</dd></div>
                        <div><dt>{{ t('topup.youSend') }}</dt><dd>{{ invoiceAmount }} {{ invoiceCoin.ticker }}</dd></div>
                        <div><dt>{{ t('topup.network') }}</dt><dd>{{ invoiceNetwork }}</dd></div>
                    </dl>
                    <p class="sum__note">{{ t('topup.creditNote') }}</p>
                </div>
            </aside>
        </div>

        <!-- ============ choice step ============ -->
        <div v-else class="steps">
                <div class="panel">
                    <h2 class="panel__title"><span class="panel__num">1</span>{{ t('topup.chooseCoin') }}</h2>
                    <p v-if="loading" class="panel__muted">{{ t('topup.loading') }}</p>
                    <p v-else-if="!coins.length" class="panel__muted">{{ t('topup.unavailable') }}</p>
                    <div v-else class="coins">
                        <button v-for="coin in coins" :key="coin.key" type="button" class="coin"
                            :class="{ 'is-selected': coin.key === coinKey }" :aria-pressed="coin.key === coinKey"
                            @click="chooseCoin(coin.key)">
                            <span class="coin__icon" :style="{ background: coin.color }">{{ coin.glyph }}</span>
                            <span class="coin__text">
                                <span class="coin__name">{{ coin.name }}</span>
                                <span class="coin__meta">{{ coin.ticker }}{{ coin.stable ? ' · ' + t('topup.stable', { ticker: coin.ticker }) : '' }}</span>
                                <span v-if="coin.networks.length > 1" class="coin__nets">{{ t('topup.networksToChoose', { n: coin.networks.length }) }}</span>
                            </span>
                        </button>
                    </div>
                </div>

                <div v-if="selectedCoin" class="panel">
                    <h2 class="panel__title"><span class="panel__num">2</span>{{ t('topup.chooseNetwork') }}</h2>
                    <div class="nets">
                        <button v-for="net in selectedCoin.networks" :key="net.label" type="button" class="net"
                            :class="{ 'is-selected': net.label === walletLabel }" :aria-pressed="net.label === walletLabel"
                            @click="walletLabel = net.label">
                            <span class="net__radio"></span>
                            <span class="net__text">
                                <span class="net__name">{{ net.name || net.chain }}</span>
                                <span v-if="net.name" class="net__chain">{{ net.chain }}</span>
                            </span>
                            <span v-if="net.fee" class="net__fee" :class="'is-' + net.feeKey">{{ feeText(net) }}</span>
                        </button>
                    </div>
                    <!-- Tron: the TRX address takes TRX only; USDT on Tron has its own wallet (USDT TRC20) -->
                    <div v-if="selectedCoin.key === 'TRX'" class="hint" role="note">
                        <v-icon icon="mdi-alert-outline" size="20" class="hint__icon"></v-icon>
                        <div class="hint__body">
                            <span class="hint__title">{{ t('topup.trxOnlyTitle') }}</span>
                            <span>{{ t('topup.trxNoUsdt') }}</span>
                            <div v-if="usdtTrc20" class="hint__row">
                                <span v-html="trxUseUsdtHtml"></span>
                                <button type="button" class="hint__btn" @click="switchToUsdtTrc20">{{ t('topup.trxSwitch') }}</button>
                            </div>
                        </div>
                    </div>
                </div>

            <!-- amount on the left half, the payment summary and the pay button on the right half -->
            <div class="payrow" :class="{ 'is-single': !selectedCoin }">
                <div v-if="selectedCoin" class="panel">
                    <h2 class="panel__title"><span class="panel__num">3</span>{{ t('topup.howMuch') }}</h2>
                    <div class="amounts">
                        <label class="amt-input" :class="{ 'is-selected': amountNum > 0 }">
                            <span>$</span>
                            <input ref="amountInput" v-model="amount" type="number" min="0" step="1" inputmode="decimal" :placeholder="t('topup.enterAmount')" @blur="tidyAmount" />
                            <span class="amt-input__cur">USD</span>
                        </label>
                    </div>
                </div>

                <div class="panel panel--sum">
                    <h2 class="panel__title">{{ t('topup.yourPayment') }}</h2>
                    <dl class="sum">
                        <div><dt>{{ t('topup.topUp') }}</dt><dd>${{ money(amountNum) }}</dd></div>
                        <div><dt>{{ t('topup.youSend') }}</dt><dd>{{ selectedWallet ? '≈ ' + estText + ' ' + selectedCoin.ticker : '—' }}</dd></div>
                        <div><dt>{{ t('topup.network') }}</dt><dd>{{ selectedNet ? networkText(selectedNet) : '—' }}</dd></div>
                    </dl>
                    <button type="button" class="btn-main" :disabled="payDisabled" @click="createInvoice">
                        {{ payLabel }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { t } from '@/i18n';
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useBitcartStore, useAccountStore, useAlertStore } from '@/stores';
import { storeToRefs } from 'pinia';
import copy from 'clipboard-copy';

const bitcartStore = useBitcartStore();
const accountStore = useAccountStore();
const { wallets, transactions } = storeToRefs(bitcartStore);
const { user } = storeToRefs(accountStore);

const OPEN_KEY = 'pp-open-topup';

// what each coin looks like; anything Bitcart offers that is not listed here still gets a plain tile
const COINS = {
    USDT: { name: 'USDT Tether', color: '#26A17B', glyph: '₮', stable: true },
    USDC: { name: 'USDC Coin', color: '#2775CA', glyph: '$', stable: true },
    BTC: { name: 'Bitcoin', color: '#F7931A', glyph: '₿', chain: 'Bitcoin network' },
    ETH: { name: 'Ethereum', color: '#627EEA', glyph: 'Ξ', chain: 'Ethereum network' },
    LTC: { name: 'Litecoin', color: '#345D9D', glyph: 'Ł', chain: 'Litecoin network' },
    TRX: { name: 'Tron', color: '#EB0029', glyph: 'T', chain: 'Tron network' },
};
const COIN_ORDER = ['USDT', 'USDC', 'BTC', 'ETH', 'LTC', 'TRX'];

// network of a wallet, read from its Bitcart label. Fee wording only where a coin has a real choice of networks.
const networkOf = (label, coinKey) => {
    if (/BEP|BSC|BINANCE/i.test(label)) return { name: 'BEP-20', chain: 'Binance Smart Chain', fee: 'Lowest fee · recommended', feeKey: 'low', rank: 1 };
    if (/ERC/i.test(label)) return { name: 'ERC20', chain: 'Ethereum network', fee: 'Medium fee', feeKey: 'mid', rank: 2 };
    if (/TRC/i.test(label)) return { name: 'TRC20', chain: 'Tron network', fee: 'Highest fee', feeKey: 'high', rank: 3 };
    return { name: '', chain: COINS[coinKey]?.chain || label, fee: '', feeKey: '', rank: 9 };
};
const networkText = (net) => net ? (net.name ? `${net.name} · ${net.chain}` : net.chain) : '';

const coins = computed(() => {
    const map = new Map();
    for (const w of wallets.value || []) {
        const label = String(w.label || w.name || '').trim();
        if (!label) continue;
        const first = label.split(/\s+/)[0].toUpperCase();
        const key = COINS[first] ? first : label;
        if (!map.has(key)) {
            const look = COINS[key] || { name: label, color: '#f5b800', glyph: label.charAt(0).toUpperCase(), stable: false };
            map.set(key, { key, ticker: COINS[key] ? key : String(w.currency || '').toUpperCase(), ...look, networks: [] });
        }
        map.get(key).networks.push({ label, rate: Number(w.rate) || 0, ...networkOf(label, key) });
    }
    const list = [...map.values()];
    for (const c of list) {
        c.networks.sort((a, b) => a.rank - b.rank);
        if (c.networks.length < 2) c.networks.forEach((n) => { n.fee = n.fee.replace(' · recommended', ''); });   // nothing to recommend between
    }
    const pos = (k) => { const i = COIN_ORDER.indexOf(k); return i < 0 ? 99 : i; };
    return list.sort((a, b) => pos(a.key) - pos(b.key));
});

const loading = ref(true);
const coinKey = ref('');
const walletLabel = ref('');       // the Bitcart wallet label; this is what the order is created with
const amount = ref('');       // always starts empty; the customer types the amount
const amountInput = ref(null);
const creating = ref(false);

const selectedCoin = computed(() => coins.value.find((c) => c.key === coinKey.value) || null);
const selectedNet = computed(() => selectedCoin.value?.networks.find((n) => n.label === walletLabel.value) || null);
const selectedWallet = computed(() => selectedNet.value);
const chooseCoin = (key) => {
    coinKey.value = key;
    walletLabel.value = selectedCoin.value?.networks[0]?.label || '';   // networks are sorted, lowest fee first
};
watch(coins, (list) => {
    if (list.length && !list.find((c) => c.key === coinKey.value)) chooseCoin(list[0].key);
}, { immediate: true });

const balance = computed(() => Number(user.value?.balance) || 0);
const amountNum = computed(() => { const n = Number(amount.value); return Number.isFinite(n) && n > 0 ? n : 0; });
const money = (v) => (Number(v) || 0).toFixed(2);
const tidyAmount = () => { if (amount.value !== '' && amount.value !== null) amount.value = amountNum.value ? Math.round(amountNum.value * 100) / 100 : ''; };

const est = computed(() => (selectedWallet.value?.rate > 0 && amountNum.value > 0) ? amountNum.value / selectedWallet.value.rate : 0);
const estText = computed(() => est.value.toFixed(selectedCoin.value?.stable ? 2 : 8));

const payDisabled = computed(() => Boolean(creating.value || !selectedWallet.value || !(est.value > 0)));
const payLabel = computed(() => {
    if (creating.value) return t('topup.btnCreating');
    if (!selectedWallet.value) return t('topup.btnChooseCoin');
    if (!(amountNum.value > 0)) return t('topup.btnEnterAmount');
    const p = { amount: estText.value, ticker: selectedCoin.value.ticker, net: selectedNet.value.name };
    return selectedNet.value.name ? t('topup.btnPayOn', p) : t('topup.btnPay', p);
});

// ---------------- the open invoice ----------------
const invoice = ref(null);
const now = ref(Date.now());
let ticker = null, poller = null;

const invoiceCoin = computed(() => {
    const label = String(invoice.value?.estCurrency || '');
    const first = label.split(/\s+/)[0].toUpperCase();
    return { ticker: COINS[first] ? first : label };
});
const invoiceNetwork = computed(() => {
    const label = String(invoice.value?.estCurrency || '');
    const first = label.split(/\s+/)[0].toUpperCase();
    return networkText(networkOf(label, COINS[first] ? first : label));
});
const invoiceAmount = computed(() => String(invoice.value?.payAmount || invoice.value?.estAmount || ''));
const expiresMs = computed(() => {
    const inv = invoice.value; if (!inv) return 0;
    return Date.parse(inv.expiresAt) || ((Date.parse(inv.createdAt) || Date.now()) + 24 * 3600 * 1000);
});
const live = ref(null);   // the gateway's view of the open invoice (topup-status), refreshed every 10 s
const invoiceStatus = computed(() => {
    if (live.value?.status) return String(live.value.status).toLowerCase();
    const row = (transactions.value || []).find((t) => t.uuid === invoice.value?.uuid);
    return String(row?.status || invoice.value?.status || '').toLowerCase();
});
const invoiceState = computed(() => {
    if (invoiceStatus.value === 'completed') return 'completed';
    if (live.value?.detected) return 'detected';   // payment on the chain, waiting for confirmations (the timer no longer matters)
    if (invoiceStatus.value === 'expired' || (expiresMs.value && now.value > expiresMs.value)) return 'expired';
    return 'waiting';
});
const stateLabel = computed(() => ({ waiting: t('topup.stateWaiting'), detected: t('topup.stateDetected'), completed: t('topup.statePaid'), expired: t('topup.stateExpired') }[invoiceState.value]));
const confirmText = computed(() => (live.value?.required > 0
    ? t('topup.confirmations', { n: Math.min(live.value.confirmations || 0, live.value.required), of: live.value.required })
    : t('topup.waitingConfirm')));
// fee wording follows the language; the network rows keep an English fallback in `fee`
const feeText = (net) => ({ low: t('topup.feeLowest'), mid: t('topup.feeMedium'), high: t('topup.feeHighest') }[net.feeKey] || net.fee);
// the network warning keeps the coin and the network in bold; both come from our own wallet list, escaped anyway
const esc = (v) => String(v || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// the USDT wallet on the Tron network, offered from the TRX hint
const usdtTrc20 = computed(() => coins.value.find((c) => c.key === 'USDT')?.networks.find((n) => n.name === 'TRC20') || null);
const switchToUsdtTrc20 = () => { if (!usdtTrc20.value) return; coinKey.value = 'USDT'; walletLabel.value = usdtTrc20.value.label; };
const trxUseUsdtHtml = computed(() => t('topup.trxUseUsdt', { coin: '<b>' + esc('USDT Tether · TRC20') + '</b>' }));
const warnHtml = computed(() => t('topup.warn', { ticker: '<b>' + esc(invoiceCoin.value.ticker) + '</b>', net: '<b>' + esc(invoiceNetwork.value) + '</b>' }));
const timeLeft = computed(() => {
    const s = Math.max(0, Math.floor((expiresMs.value - now.value) / 1000));
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
});

const remember = (inv) => { try { inv ? sessionStorage.setItem(OPEN_KEY, JSON.stringify(inv)) : sessionStorage.removeItem(OPEN_KEY); } catch (e) { /* storage is optional */ } };
const stopWatching = () => { clearInterval(ticker); clearInterval(poller); ticker = poller = null; };
const checkLive = async () => {
    const uuid = invoice.value?.uuid;
    if (!uuid) return;
    const s = await bitcartStore.topupStatus(uuid);
    if (s && invoice.value?.uuid === uuid) live.value = s;
};
const startWatching = () => {
    stopWatching();
    live.value = null;
    ticker = setInterval(() => { now.value = Date.now(); }, 1000);
    checkLive();
    poller = setInterval(() => { if (invoiceState.value === 'waiting' || invoiceState.value === 'detected') checkLive(); }, 10000);
};
watch(invoiceState, (state, before) => {
    if (state === 'completed' && (before === 'waiting' || before === 'detected')) { accountStore.getAccount(); remember(null); }
    if (state !== before) bitcartStore.loadTransactionHistory({ from: 0, count: 50 });
    if (state === 'completed' || state === 'expired') clearInterval(poller);
});

const createInvoice = async () => {
    if (payDisabled.value) return;
    creating.value = true;
    try {
        const res = await bitcartStore.createBitcartTransaction(walletLabel.value, amountNum.value, est.value.toFixed(8));
        if (res && res.address) {
            // keep only what the payment step shows (the full row also carries the receipt HTML)
            const { uuid, amount: usd, status, createdAt, address, qrCode, estAmount, estCurrency, payAmount, expiresAt } = res;
            invoice.value = { uuid, amount: usd, status, createdAt, address, qrCode, estAmount, estCurrency, payAmount, expiresAt };
            remember(invoice.value);
            startWatching();
            bitcartStore.loadTransactionHistory({ from: 0, count: 50 });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    } finally {
        creating.value = false;
    }
};
const closeInvoice = () => { invoice.value = null; live.value = null; amount.value = ''; remember(null); stopWatching(); };

const copyText = (text, message) => { copy(String(text || '')); useAlertStore().success(message); };

onMounted(async () => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(OPEN_KEY) || 'null');
        if (saved && saved.uuid && saved.address) { invoice.value = saved; startWatching(); }
    } catch (e) { /* nothing saved */ }
    await bitcartStore.getWallets();
    loading.value = false;
});
onBeforeUnmount(stopWatching);
</script>

<style scoped>
/* 40% narrower than the page on a wide screen; never below 760px, so laptops and phones keep the full width */
.topup { color: var(--ui-ink); margin: 0 auto 16px; width: max(60%, min(100%, 760px)); }
.topup__head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 12px 24px; margin-bottom: 16px; }
.topup__title { margin: 0; font-size: 22px; font-weight: 600; color: var(--ui-ink); }
.topup__sub { margin: 4px 0 0; font-size: 14px; color: var(--ui-dim); }
.topup__balance { display: flex; flex-direction: column; align-items: flex-end; padding: 8px 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card); }
.topup__balance span { font-size: 12px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ui-dim); }
.topup__balance strong { font-size: 22px; font-weight: 600; color: var(--ui-teal); font-variant-numeric: tabular-nums; }

.topup__grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 16px; align-items: start; }
.steps, .side { display: grid; gap: 16px; }
.payrow { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; align-items: stretch; }
/* both boxes are the same height: the amount field sits in the middle of its box, a little larger */
.payrow > .panel { display: flex; flex-direction: column; }
.payrow .amounts { flex: 1; align-items: center; }
.payrow .amt-input { font-size: 18px; }
.payrow .amt-input input { height: 56px; font-size: 20px; }
.payrow .panel--sum .btn-main { margin-top: auto; }
.payrow .panel--sum .sum { margin-bottom: 14px; }
.payrow.is-single { grid-template-columns: minmax(0, 1fr); }
@media (max-width: 760px) { .payrow { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 1100px) { .topup__grid { grid-template-columns: minmax(0, 1fr); } }

.panel { background: var(--ui-card); border: 1px solid var(--ui-line); border-radius: 12px; padding: 16px; }
.panel__title { display: flex; align-items: center; gap: 10px; margin: 0 0 12px; font-size: 16px; font-weight: 600; color: var(--ui-ink); }
.panel__num { display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; background: var(--ui-pri); color: #ffffff; font-size: 13px; }
.panel__muted { margin: 0; font-size: 13.5px; color: var(--ui-dim); }
.panel__muted--after { margin-top: 10px; }

/* coin tiles */
.coins { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
@media (max-width: 900px) { .coins { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 520px) { .coins { grid-template-columns: minmax(0, 1fr); } }
.coin { position: relative; display: flex; align-items: center; gap: 12px; min-height: 68px; padding: 12px 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card-2); color: var(--ui-ink); text-align: left; cursor: pointer; transition: border-color 150ms ease, transform 150ms ease; }
.coin:hover, .coin:focus-visible { border-color: var(--ui-pri-hover); transform: translateY(-1px); outline: none; }
.coin.is-selected { border: 2px solid var(--ui-pri); background: var(--ui-pri-soft); padding: 11px 13px; }
.coin__icon { flex: none; display: grid; place-items: center; width: 40px; height: 40px; border-radius: 50%; color: #ffffff; font-size: 20px; font-weight: 700; line-height: 1; }
.coin__text { display: flex; flex-direction: column; min-width: 0; }
.coin__name { font-size: 15px; font-weight: 600; line-height: 1.25; }
.coin__meta { font-size: 12.5px; color: var(--ui-dim); line-height: 1.3; }
.coin__nets { font-size: 12px; line-height: 1.3; color: var(--ui-teal); }

/* network rows */
.nets { display: grid; gap: 8px; }
.net { display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-card-2); color: var(--ui-ink); text-align: left; cursor: pointer; transition: border-color 150ms ease; }
.net:hover, .net:focus-visible { border-color: var(--ui-pri-hover); outline: none; }
.net.is-selected { border: 2px solid var(--ui-pri); background: var(--ui-pri-soft); padding: 11px 13px; }
.net__radio { flex: none; width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--ui-line-strong); }
.net.is-selected .net__radio { border-color: var(--ui-pri); background: radial-gradient(circle, #ffffff 0 35%, var(--ui-pri) 40%); }
.net__text { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 10px; flex: 1; min-width: 0; }
.net__name { font-size: 15px; font-weight: 600; }
.net__chain { font-size: 13px; color: var(--ui-dim); }
.net__fee { flex: none; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.net__fee.is-low { background: var(--ui-ok-soft); color: var(--ui-ok-ink); }
.net__fee.is-mid { background: var(--ui-line); color: var(--ui-dim); }
.net__fee.is-high { background: var(--ui-warn-soft); color: var(--ui-warn-ink); }
.net-locked { display: flex; align-items: center; gap: 8px; margin: 0; padding: 12px 14px; border: 1px dashed var(--ui-line); border-radius: 12px; font-size: 14px; color: var(--ui-dim); }
.net-locked b { color: var(--ui-ink); font-weight: 600; }

/* amounts */
.amounts { display: flex; flex-wrap: wrap; gap: 8px; }
.amt { min-width: 72px; padding: 10px 14px; border: 1px solid var(--ui-line); border-radius: 10px; background: var(--ui-card-2); color: var(--ui-ink); font-size: 15px; font-weight: 600; cursor: pointer; font-variant-numeric: tabular-nums; }
.amt:hover, .amt:focus-visible { border-color: var(--ui-pri-hover); outline: none; }
.amt.is-selected { border-color: var(--ui-pri); background: var(--ui-pri); }
.amt-input { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 190px; margin: 0; padding: 0 12px; border: 1px solid var(--ui-line); border-radius: 10px; background: var(--ui-card-2); color: var(--ui-dim); font-size: 15px; }
.amt-input:focus-within, .amt-input.is-selected { border-color: var(--ui-pri); }
.amt-input input { flex: 1; min-width: 0; height: 42px; border: 0; outline: none; background: transparent; color: var(--ui-ink); font-size: 15px; font-variant-numeric: tabular-nums; }
.amt-input input::placeholder { color: var(--ui-placeholder); }
.amt-input__cur { font-size: 12px; letter-spacing: 0.05em; }

/* summary */
.sum { margin: 0; display: grid; gap: 8px; font-size: 14px; }
.sum > div { display: flex; justify-content: space-between; gap: 12px; }
.sum dt { color: var(--ui-dim); font-weight: 400; }
.sum dd { margin: 0; color: var(--ui-ink); text-align: right; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.sum__total { padding-top: 8px; border-top: 1px solid var(--ui-line); }
.sum__total dd { color: var(--ui-teal); font-weight: 600; }
.sum__note { margin: 10px 0 0; font-size: 12.5px; line-height: 1.45; color: var(--ui-dim); }
.btn-main { width: 100%; margin-top: 14px; min-height: 44px; padding: 10px 14px; border: 0; border-radius: 10px; background: var(--ui-pri); color: #ffffff; font-size: 15px; font-weight: 600; cursor: pointer; transition: background-color 150ms ease; }
.btn-main:hover:not(:disabled) { background: var(--ui-pri-hover); }
.btn-main:disabled { background: var(--ui-card); color: var(--ui-dim); cursor: not-allowed; }
.btn-ghost { padding: 8px 14px; border: 1px solid var(--ui-line); border-radius: 10px; background: transparent; color: var(--ui-ink); font-size: 14px; cursor: pointer; }
.btn-ghost:hover { border-color: var(--ui-pri-hover); }
.how { margin: 0; padding-left: 18px; display: grid; gap: 6px; font-size: 13.5px; line-height: 1.45; color: var(--ui-dim); list-style: decimal; }

/* payment step */
.pay__top { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.pay__top .panel__title { margin: 0; }
.pay__state { padding: 3px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.pay__state.is-waiting { background: var(--ui-line); color: var(--ui-dim); }
.pay__state.is-detected { background: var(--ui-warn-soft, #fff4d6); color: var(--ui-warn-ink, #7a5200); }
.pay__state.is-completed { background: var(--ui-ok); color: #ffffff; }
.pay__state.is-expired { background: var(--ui-dan); color: #ffffff; }
.pay__body { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-start; }
.pay__qr { width: 176px; height: 176px; border-radius: 10px; background: #ffffff; padding: 6px; }
.pay__fields { flex: 1; min-width: 260px; display: grid; gap: 12px; }
.field__label { display: block; margin-bottom: 4px; font-size: 12px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--ui-dim); }
.field__row { display: flex; align-items: center; gap: 8px; }
.field__value { flex: 1; min-width: 0; padding: 9px 12px; border: 1px solid var(--ui-line); border-radius: 8px; background: var(--ui-card-2); color: var(--ui-ink); font-size: 14px; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.field__value--addr { font-size: 13px; }
.field__plain { font-size: 15px; font-weight: 600; color: var(--ui-ink); }
.copy { flex: none; display: inline-flex; align-items: center; gap: 4px; padding: 8px 12px; border: 1px solid var(--ui-line); border-radius: 8px; background: transparent; color: var(--ui-ink); font-size: 13px; cursor: pointer; }
.copy:hover { border-color: var(--ui-pri-hover); }
.pay__warn { display: flex; gap: 8px; align-items: flex-start; margin: 14px 0 0; padding: 10px 12px; border: 1px solid var(--ui-warn); border-radius: 10px; background: var(--ui-warn-soft); font-size: 13.5px; color: var(--ui-warn-ink); }
.pay__warn b { color: var(--ui-warn-ink); font-weight: 600; }
/* TRX: do not send USDT here */
.hint { display: flex; gap: 12px; align-items: flex-start; margin-top: 10px; padding: 12px 14px; border: 1px solid var(--ui-warn); border-radius: 10px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); }
.hint__icon { flex: none; margin-top: 1px; }
.hint__body { flex: 1; min-width: 0; display: grid; gap: 4px; font-size: 13.5px; }
.hint__title { font-size: 14px; font-weight: 700; }
.hint__body b { font-weight: 700; color: var(--ui-warn-ink); }
.hint__row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 12px; margin-top: 4px; }
.hint__btn { flex: none; padding: 7px 14px; border: 0; border-radius: 8px; background: #26A17B; color: #ffffff; font-size: 13px; font-weight: 700; cursor: pointer; }
.hint__btn:hover, .hint__btn:focus-visible { background: #1f8a69; outline: none; }
.track { list-style: none; margin: 16px 0 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; font-size: 13px; color: var(--ui-dim); }
@media (max-width: 640px) { .track { grid-template-columns: minmax(0, 1fr); } }
.track li { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid var(--ui-line); border-radius: 10px; }
.track__dot { flex: none; display: grid; place-items: center; width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--ui-line-strong); color: var(--ui-badge-ink); }
.track li.is-done { color: var(--ui-ink); }
.track li.is-done .track__dot { background: var(--ui-ok); border-color: var(--ui-ok); }
.track li.is-now { color: var(--ui-ink); border-color: var(--ui-pri); }
.track li.is-now .track__dot { border-color: var(--ui-pri); animation: topup-pulse 1.6s ease-in-out infinite; }
@keyframes topup-pulse { 50% { box-shadow: 0 0 0 5px rgba(101, 96, 240, 0.25); } }
@media (prefers-reduced-motion: reduce) { .track li.is-now .track__dot { animation: none; } .coin, .net { transition: none; } }
.pay__foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin-top: 16px; }
.pay__timer { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: var(--ui-ink); font-variant-numeric: tabular-nums; }
.pay__hint { margin: 10px 0 0; font-size: 12.5px; color: var(--ui-dim); }
</style>
